import type { ReactNode } from 'react';
import { createContext, useContext, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type {
  ConditionKind,
  NodePath,
} from '@/components/tree-dnd/tree-dnd-types';
import { isGroupNode } from '@/components/tree-dnd/tree-dnd-types';
import { encodeItemId, parseItemId } from '@/components/tree-dnd/tree-dnd-path';
import { getNodeAtPath } from '@/components/tree-dnd/tree-dnd-utils';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  treeCollisionDetection,
  getDeepestContainerPath,
} from '@/components/tree-dnd/tree-dnd-collision';
import {
  resolveDropIndicator,
  type DropIndicator,
} from '@/components/tree-dnd/tree-dnd-drop-indicator';
import { useTreeDndStore } from '@/components/tree-dnd/tree-dnd-store';
import { useTreeDndSensors } from '@/components/tree-dnd/tree-dnd-sensors';

interface TreeDndDragContextValue {
  isDragging: boolean;
  highlightedContainerPath: NodePath | null;
  dropIndicator: DropIndicator | null;
}

const TreeDndDragContext = createContext<TreeDndDragContextValue>({
  isDragging: false,
  highlightedContainerPath: null,
  dropIndicator: null,
});

export function useTreeDndHighlight() {
  return useContext(TreeDndDragContext).highlightedContainerPath;
}

export function useTreeDndDropIndicator() {
  return useContext(TreeDndDragContext).dropIndicator;
}

interface TreeDndProviderProps {
  children: ReactNode;
}

export function TreeDndProvider({ children }: TreeDndProviderProps) {
  const { LL } = useI18nContext();
  const tree = useTreeDndStore((state) => state.tree);
  const moveNode = useTreeDndStore((state) => state.moveNode);

  const conditionLabels: Record<ConditionKind, () => string> = {
    installedApp: LL.audiences.filters.conditions.installedApp,
    boughtProduct: LL.audiences.filters.conditions.boughtProduct,
    receivedNotification: LL.audiences.filters.conditions.receivedNotification,
    clickedCampaign: LL.audiences.filters.conditions.clickedCampaign,
    rfmGroup: LL.audiences.filters.conditions.rfmGroup,
    lastNotificationAt: LL.audiences.filters.conditions.lastNotificationAt,
    lastAppAccessAt: LL.audiences.filters.conditions.lastAppAccessAt,
    viewedProduct: LL.audiences.filters.conditions.viewedProduct,
    location: LL.audiences.filters.conditions.location,
    lastOrder: LL.audiences.filters.conditions.lastOrder,
    orderFrequency: LL.audiences.filters.conditions.orderFrequency,
    totalSpend: LL.audiences.filters.conditions.totalSpend,
    hasNotificationsEnabled:
      LL.audiences.filters.conditions.hasNotificationsEnabled,
  };

  const [activePath, setActivePath] = useState<NodePath | null>(null);
  const [highlightedContainerPath, setHighlightedContainerPath] =
    useState<NodePath | null>(null);
  const [dropIndicator, setDropIndicator] = useState<DropIndicator | null>(
    null
  );

  const sensors = useTreeDndSensors();

  const activeNode = activePath ? getNodeAtPath(tree, activePath) : null;

  const clearDragState = () => {
    setActivePath(null);
    setHighlightedContainerPath(null);
    setDropIndicator(null);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActivePath(parseItemId(event.active.id));
  };

  const handleDragOver = (event: DragOverEvent) => {
    const collisions = event.collisions ?? [];
    const sourcePath = parseItemId(event.active.id);
    if (!sourcePath) return;

    const indicator = resolveDropIndicator(collisions, sourcePath, tree);
    setDropIndicator(indicator);
    setHighlightedContainerPath(
      indicator ? getDeepestContainerPath(collisions) : null
    );
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, collisions } = event;
    clearDragState();

    if (!collisions || collisions.length === 0) return;

    const sourcePath = parseItemId(active.id);
    if (!sourcePath) return;

    const indicator = resolveDropIndicator(collisions, sourcePath, tree);
    if (!indicator) return;

    moveNode(sourcePath, indicator.parentPath, indicator.index);
  };

  const handleDragCancel = () => {
    clearDragState();
  };

  return (
    <TreeDndDragContext.Provider
      value={{
        isDragging: activePath !== null,
        highlightedContainerPath,
        dropIndicator,
      }}
    >
      <DndContext
        sensors={sensors}
        collisionDetection={treeCollisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {children}
        <DragOverlay dropAnimation={null}>
          {activeNode ? (
            isGroupNode(activeNode) ? (
              <div className="h-10 w-48 rounded-lg border bg-card shadow-md" />
            ) : (
              <div className="rounded-lg border bg-card px-3 py-2 text-sm font-medium shadow-md">
                {conditionLabels[activeNode.kind]()}
              </div>
            )
          ) : null}
        </DragOverlay>
      </DndContext>
    </TreeDndDragContext.Provider>
  );
}

export function TreeDndSortableList({
  paths,
  children,
}: {
  paths: NodePath[];
  children: ReactNode;
}) {
  return (
    <SortableContext
      items={paths.map(encodeItemId)}
      strategy={verticalListSortingStrategy}
    >
      {children}
    </SortableContext>
  );
}

export function getChildPaths(parentPath: NodePath, count: number): NodePath[] {
  return Array.from({ length: count }, (_, index) => [...parentPath, index]);
}
